/**
 * "Is this deck's group link still live?" — the ONE predicate behind every
 * surface that routes a personal deck's behaviour into a study group.
 *
 * A deck shared into a group keeps its `linkedGroups` entry forever, including
 * after the sharer leaves. That is deliberate: the group keeps the deck (a
 * leave deletes nothing), the SG row keeps `addedByUserId`, and the surviving
 * link is what hands the deck straight back to its owner if they rejoin — no
 * repair step, no re-share.
 *
 * But while the viewer is OUT of the group, every group-scoped behaviour has to
 * go dormant. A link read as live opens a WebSocket the group's DO closes with
 * 4403 ("Not a member of this group"), and the user's own deck, in their own
 * personal library, renders a connection error where its notes should be.
 *
 * So: read the link through these helpers, never `linkedGroups[0]` directly.
 * Dormant, not deleted — that is what makes leaving and rejoining symmetric.
 */
import { useMemo } from 'react';

import { getGroupMembershipsNow, useActiveGroupMemberships } from './helpers';

/** The membership shape these helpers need — a soft-deleted row is not a membership. */
type MembershipLike = { groupId: string; deletedTimeISO?: string | null };

/** The link shape these helpers need. Generic so callers keep their own richer type. */
type LinkLike = { groupId: string };

/** Is the viewer still in this group? `memberships` may include soft-deleted rows. */
export function isActiveGroupMember(
  groupId: string | null | undefined,
  memberships: readonly MembershipLike[],
): boolean {
  if (!groupId) return false;
  return memberships.some((m) => m.groupId === groupId && !m.deletedTimeISO);
}

/**
 * The deck's first link to a group the viewer is STILL a member of, or `null`
 * — for a purely personal deck, and equally for one whose every link points at
 * a group the viewer has left (an "orphaned" deck: it behaves personal again).
 */
export function pickActiveLinkedGroup<T extends LinkLike>(
  links: readonly T[] | null | undefined,
  memberships: readonly MembershipLike[],
): T | null {
  if (!links?.length) return null;
  return links.find((link) => isActiveGroupMember(link.groupId, memberships)) ?? null;
}

/** Snapshot {@link pickActiveLinkedGroup} against the store's memberships right now. */
export function getActiveLinkedGroupNow<T extends LinkLike>(
  links: readonly T[] | null | undefined,
): T | null {
  return pickActiveLinkedGroup(links, getGroupMembershipsNow());
}

/** Reactive {@link pickActiveLinkedGroup} against the viewer's live memberships. */
export function useActiveLinkedGroup<T extends LinkLike>(
  links: readonly T[] | null | undefined,
): T | null {
  const memberships = useActiveGroupMemberships();
  return useMemo(() => pickActiveLinkedGroup(links, memberships), [links, memberships]);
}

/** Reactive {@link isActiveGroupMember} against the viewer's live memberships. */
export function useIsActiveGroupMember(groupId: string | null | undefined): boolean {
  const memberships = useActiveGroupMemberships();
  return useMemo(() => isActiveGroupMember(groupId, memberships), [groupId, memberships]);
}
